// frontend/src/components/ActivityLightbox.js

import React, { useEffect, useMemo, useState } from "react";
import { X } from "phosphor-react";
import { api } from "api";
import ActivityCard from "./ActivityCard";

export default function ActivityLightbox({
  open,
  onClose,
  activityId,
  activity,     
  currentUser,   // used for user-hydration + ActivityCard
  className = "",
}) {
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(null);

  // prefer the passed `activity` if present
  const initial = useMemo(() => activity || null, [activity]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!open) return;

      // no fetch when the activity is already provided
      if (initial) {
        if (!cancelled) setLoaded(initial);
        return;
      }
      if (!activityId) return;

      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

        const tryGet = async (path) =>
          api.get(path, { headers }).then((r) => r?.data).catch(() => null);

        const data =
          (await tryGet(`/activities/${activityId}`)) ||
          (await tryGet(`/activity/${activityId}`)) ||
          (await tryGet(`/api/activities/${activityId}`)) ||
          null;

        if (!cancelled) setLoaded(data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [open, activityId, initial]);

  // hydrate missing user to avoid “unknown user”
  const hydrated = useMemo(() => {
    const base = loaded || initial;
    if (!base) return base;
    if (base.user || !currentUser) return base;

    const u = currentUser;
    return {
      ...base,
      user: {
        id: u.id,
        name: u.name || u.username || `User #${u.id}`,
        profile_pic_url: u.profile_pic_url || u.avatar_url || null,
      },
    };
  }, [loaded, initial, currentUser]);

  // Tag click -> go to /tags/:tag (no change to ActivityCard needed)
  const handleTagClick = (t) => {
    if (!t) return;
    // Close the lightbox first (optional but feels cleaner)
    onClose?.();
    // Navigate to TagFeed page
    window.location.assign(`/tags/${encodeURIComponent(String(t))}`);
  };

  if (!open) return null;

  return (
    <div className={`fixed inset-0 z-[200] grid place-items-center bg-black/40 ${className}`}>
      {/* Outer card clips the inner scroller so the scrollbar sits inside the rounded corners */}
      <div className="bg-white rounded-3xl w-full pb-4 max-w-[380px] sm:max-w-xl shadow-[0_8px_0px_rgba(37,99,235,0.96)] overflow-hidden">
        {/* Header (not scrolling) */}
        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="grid !min-h-0 !h-9 w-9 pt-4 pr-10 text-gray-700 transition"
            aria-label="Close"
          >
            <X
              size={16}
              className="sm:!w-5 sm:!h-5"
              weight="bold"
            />
          </button>
        </div>

        {/* Scrollable body — scrollbar is now clipped by the rounded outer */}
        <div className="max-h-[85vh] overflow-auto rounded-scroll pr-2 sm:pr-3">
          <div className="px-2 sm:px-4 pb-2">
            {loading && <div className="text-sm text-gray-500">Loading…</div>}
            {!loading && !hydrated && (
              <div className="text-sm text-red-600">Activity not found</div>
            )}
            {!loading && hydrated && (
              <ActivityCard
                activity={hydrated}
                currentUser={currentUser}
                onTagClick={handleTagClick} 
                contentClassName="px-4 pb-3 pt-2 sm:pb-4 sm:pt-3"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
